import React, { useState, useEffect } from 'react';
import './admindashboard.css';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import Loader from '../Loader';
import { FaCheckCircle, FaTimesCircle, FaUsers, FaHistory, FaChartLine, FaBitcoin, FaToggleOn, FaMoneyBillWave } from 'react-icons/fa';

const Admindashboard = ({ route }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState([]);
  const [stakes, setStakes] = useState([]);
  const [users, setUsers] = useState([]);
  const [cryptoAssets, setCryptoAssets] = useState([]);
  const [stakingEnabled, setStakingEnabled] = useState(true);
  const [activeView, setActiveView] = useState('transactions'); // transactions, stakes, users, crypto
  const [activeTab, setActiveTab] = useState('pending'); // pending, completed, rejected
  const [selectedType, setSelectedType] = useState('all'); // all, deposit, withdrawal
  const [showAddAssetModal, setShowAddAssetModal] = useState(false);
  const [newAsset, setNewAsset] = useState({
    name: '',
    symbol: '',
    walletAddress: '',
    logoUrl: ''
  });
  const [showFundUserModal, setShowFundUserModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [fundingAmount, setFundingAmount] = useState('');

  console.log(transactions)
  const Toast = Swal.mixin({
    toast: true,
    position: 'top-end',
    showConfirmButton: false,
    timer: 3000,
    timerProgressBar: true,
  });

  // Check admin status when component mounts
  useEffect(() => {
    const checkAdminStatus = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${route}/auth/getData`, {
          headers: {
            'x-access-token': token
          }
        });

        if (!response.data.isAdmin) {
          Toast.fire({
            icon: 'error',
            title: 'Access Denied: Admin privileges required'
          });
          navigate('/dashboard');
          return;
        }
        setLoading(false);
      } catch (error) {
        console.error('Error checking admin status:', error);
        Toast.fire({
          icon: 'error',
          title: 'Authentication failed'
        });
        navigate('/dashboard');
      }
    };

    checkAdminStatus();
  }, [route, navigate]);

  useEffect(() => {
    if (activeView === 'transactions') {
      fetchTransactions();
    } else if (activeView === 'stakes') {
      fetchStakes();
    } else if (activeView === 'users') {
      fetchUsers();
    } else if (activeView === 'crypto') {
      fetchCryptoAssets();
    }
  }, [activeView, activeTab, selectedType]);

  useEffect(() => {
    fetchStakingStatus();
  }, []);

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      console.log(token)
      const response = await axios.get(
        `${route}/admin/transactions?status=${activeTab}&type=${selectedType}`,
        {
          headers: {
            'x-access-token': token
          }
        }
      );

      setTransactions(response.data);
    } catch (error) {
      console.error('Error fetching transactions:', error);
      if (error.response?.status === 401) {
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchStakes = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.get(
        `${route}/admin/stakes`,
        {
        headers: {
            'x-access-token': token
          }
        }
      );

      setStakes(response.data);
    } catch (error) {
      console.error('Error fetching stakes:', error);
      if (error.response?.status === 401) {
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.get(
        `${route}/admin/users`,
        {
      headers: {
            'x-access-token': token
          }
        }
      );

      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      if (error.response?.status === 401) {
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchCryptoAssets = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.get(
        `${route}/crypto-assets/all`,
        {
          headers: {
            'x-access-token': token
          }
        }
      );

      setCryptoAssets(response.data.assets);
    } catch (error) {
      console.error('Error fetching crypto assets:', error);
      if (error.response?.status === 401) {
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchStakingStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${route}/admin/settings`, {
        headers: {
          'x-access-token': token
        }
      });
      setStakingEnabled(response.data.settings.stakingEnabled);
    } catch (error) {
      console.error('Error fetching staking status:', error);
    }
  };

  const handleToggleStaking = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${route}/admin/settings/toggle-staking`,
        {}, // Empty object for request body
        {
          headers: {
            'x-access-token': token
          }
        }
      );

      if (response.data.status === 'ok') {
        setStakingEnabled(response.data.stakingEnabled);
        Toast.fire({
          icon: 'success',
          title: response.data.message
        });
      }
    } catch (error) {
      Toast.fire({
        icon: 'error',
        title: error.response?.data?.message || 'Failed to toggle staking'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (transactionId, action, reason = '') => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Get the transaction details to determine if it's a withdrawal or deposit
      const transaction = transactions.find(t => t._id === transactionId);
      const isWithdrawal = transaction.type === 'withdrawal';
      
      const endpoint = action === 'confirm' 
        ? (isWithdrawal ? 'withdrawal/confirm' : 'ledger/confirm-payment')
        : (isWithdrawal ? 'withdrawal/reject' : 'ledger/reject-payment');

      const response = await axios.post(
        `${route}/${endpoint}`,
        {
          transactionId,
          reason
        },
        {
      headers: {
            'x-access-token': token
          }
        }
      );

      if (response.data.status === 'ok') {
      Toast.fire({
          icon: 'success',
          title: `${isWithdrawal ? 'Withdrawal' : 'Transaction'} ${action === 'confirm' ? 'confirmed' : 'rejected'} successfully`
      });
        fetchTransactions();
      }
    } catch (error) {
      Toast.fire({
        icon: 'error',
        title: error.response?.data?.message || 'Action failed'
      });
    } finally {
      setLoading(false);
    }
  };

  const confirmAction = (transactionId) => {
    const transaction = transactions.find(t => t._id === transactionId);
    const isWithdrawal = transaction.type === 'withdrawal';

    Swal.fire({
      title: `Confirm ${isWithdrawal ? 'Withdrawal' : 'Deposit'}`,
      text: `Are you sure you want to confirm this ${isWithdrawal ? 'withdrawal' : 'deposit'}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, confirm it!',
      cancelButtonText: 'No, cancel'
    }).then((result) => {
      if (result.isConfirmed) {
        handleAction(transactionId, 'confirm');
      }
    });
  };

  const rejectAction = (transactionId) => {
    const transaction = transactions.find(t => t._id === transactionId);
    const isWithdrawal = transaction.type === 'withdrawal';

    Swal.fire({
      title: `Reject ${isWithdrawal ? 'Withdrawal' : 'Deposit'}`,
      text: 'Please provide a reason for rejection',
      input: 'text',
      showCancelButton: true,
      confirmButtonText: 'Reject',
      showLoaderOnConfirm: true,
      preConfirm: (reason) => {
        return handleAction(transactionId, 'reject', reason);
      }
    });
  };

  const handleAddAsset = async () => {
    try {
      if (!newAsset.name || !newAsset.symbol || !newAsset.walletAddress) {
        Toast.fire({
          icon: 'error',
          title: 'Name, symbol, and wallet address are required'
        });
        return;
      }

      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.post(
        `${route}/crypto-assets/add`,
        newAsset,
        {
          headers: {
            'x-access-token': token
          }
        }
      );

      if (response.data.status === 'ok') {
        Toast.fire({
          icon: 'success',
          title: 'Crypto asset added successfully'
        });
        setShowAddAssetModal(false);
        setNewAsset({
          name: '',
          symbol: '',
          walletAddress: '',
          logoUrl: ''
        });
        fetchCryptoAssets();
      }
    } catch (error) {
      Toast.fire({
        icon: 'error',
        title: error.response?.data?.message || 'Failed to add crypto asset'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateAsset = async (id, updates) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.put(
        `${route}/api/crypto-assets/${id}`,
        updates,
        {
          headers: {
            'x-access-token': token
          }
        }
      );

      if (response.data.status === 'ok') {
        Toast.fire({
          icon: 'success',
          title: response.data.message || 'Crypto asset updated successfully'
        });
        fetchCryptoAssets();
      }
    } catch (error) {
      console.error('Error updating crypto asset:', error);
      Toast.fire({
        icon: 'error',
        title: error.response?.data?.message || 'Failed to update crypto asset'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAsset = async (id) => {
    try {
      const result = await Swal.fire({
        title: 'Delete Crypto Asset',
        text: 'Are you sure you want to delete this crypto asset?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, delete it!',
        cancelButtonText: 'No, cancel'
      });

      if (result.isConfirmed) {
        setLoading(true);
        const token = localStorage.getItem('token');
        
        const response = await axios.delete(
          `${route}/api/crypto-assets/${id}`,
          {
            headers: {
              'x-access-token': token
            }
          }
        );

        if (response.data.status === 'ok') {
          Toast.fire({
            icon: 'success',
            title: response.data.message || 'Crypto asset deleted successfully'
          });
          fetchCryptoAssets();
        }
      }
    } catch (error) {
      console.error('Error deleting crypto asset:', error);
      Toast.fire({
        icon: 'error',
        title: error.response?.data?.message || 'Failed to delete crypto asset'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleManualFunding = async () => {
    try {
      if (!selectedUser || !fundingAmount || parseFloat(fundingAmount) <= 0) {
        Toast.fire({
          icon: 'error',
          title: 'Please enter a valid amount'
        });
        return;
      }

      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.post(
        `${route}/admin/manual-fund`,
        {
          userId: selectedUser._id,
          amount: parseFloat(fundingAmount)
        },
        {
          headers: {
            'x-access-token': token
          }
        }
      );

      if (response.data.status === 'ok') {
        Toast.fire({
          icon: 'success',
          title: 'User ledger funded successfully'
        });
        setShowFundUserModal(false);
        setSelectedUser(null);
        setFundingAmount('');
        fetchUsers(); // Refresh users list
      }
    } catch (error) {
      Toast.fire({
        icon: 'error',
        title: error.response?.data?.message || 'Failed to fund user ledger'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-dashboard">
      {loading && <Loader />}
      
      <div className="admin-header">
        <h1>Admin Dashboard</h1>
        <div className="system-controls">
          <div className="staking-control">
            <span>Staking Status:</span>
            <button 
              className={`toggle-btn ${stakingEnabled ? 'enabled' : 'disabled'}`}
              onClick={handleToggleStaking}
            >
              <FaToggleOn className={stakingEnabled ? 'enabled' : ''} />
              {stakingEnabled ? 'Enabled' : 'Disabled'}
            </button>
          </div>
        </div>
        <div className="view-controls">
          <button 
            className={activeView === 'transactions' ? 'active' : ''} 
            onClick={() => setActiveView('transactions')}
          >
            <FaHistory /> Funding History
          </button>
          <button 
            className={activeView === 'stakes' ? 'active' : ''} 
            onClick={() => setActiveView('stakes')}
          >
            <FaChartLine /> Staking History
          </button>
          <button 
            className={activeView === 'users' ? 'active' : ''} 
            onClick={() => setActiveView('users')}
          >
            <FaUsers /> Users
          </button>
          <button 
            className={activeView === 'crypto' ? 'active' : ''} 
            onClick={() => setActiveView('crypto')}
          >
            <FaBitcoin /> Crypto Assets
          </button>
        </div>

        {activeView === 'transactions' && (
          <div className="filter-controls">
            <div className="tab-buttons">
              <button 
                className={activeTab === 'pending' ? 'active' : ''} 
                onClick={() => setActiveTab('pending')}
              >
                Pending
              </button>
              <button 
                className={activeTab === 'completed' ? 'active' : ''} 
                onClick={() => setActiveTab('completed')}
              >
                Completed
              </button>
              <button 
                className={activeTab === 'rejected' ? 'active' : ''} 
                onClick={() => setActiveTab('rejected')}
              >
                Rejected
              </button>
            </div>
            <select 
              value={selectedType} 
              onChange={(e) => setSelectedType(e.target.value)}
            >
              <option value="all">All Types</option>
              <option value="deposit">Deposits</option>
              <option value="withdrawal">Withdrawals</option>
            </select>
          </div>
        )}
      </div>

      <div className="admin-content">
        {activeView === 'transactions' && (
          <div className="transactions-container">
            {transactions.length === 0 ? (
              <div className="no-data">
                <p>No {activeTab} transactions found</p>
              </div>
            ) : (
              <div className="transactions-grid">
                {transactions.map((transaction) => (
                  <div key={transaction._id} className="transaction-card">
                    <div className="transaction-header">
                      <h3>{transaction.type.toUpperCase()}</h3>
                      <span className={`status ${transaction.status}`}>
                        {transaction.status}
                      </span>
                    </div>
                    <div className="transaction-details">
                      <p><strong>User:</strong> {transaction.user.email}</p>
                      <p><strong>Amount:</strong> ${transaction.amount.toFixed(2)}</p>
                      <p><strong>Method:</strong> {transaction.method}</p>
                      <p>
                        <strong>Date:</strong>{' '}
                        {new Date(transaction.createdAt).toLocaleDateString()}
                      </p>
                      {transaction.wallet && (
                        <p><strong>Wallet:</strong> {transaction.wallet}</p>
                      )}
                      {transaction.proofImage && (
                        <div className="proof-image">
                          <strong>Proof:</strong>
                          <a 
                            href={transaction.proofImage} 
                            target="_blank" 
                            rel="noopener noreferrer"
                          >
                            View Proof
                          </a>
                        </div>
                      )}
                    </div>
                    {activeTab === 'pending' && (
                      <div className="action-buttons">
                      <button
                          className="confirm-btn"
                          onClick={() => confirmAction(transaction._id)}
                        >
                          <FaCheckCircle /> Confirm
                      </button>
                      <button
                          className="reject-btn"
                          onClick={() => rejectAction(transaction._id)}
                      >
                          <FaTimesCircle /> Reject
                      </button>
                    </div>
                    )}
                  </div>
                ))}
              </div>
            )}
                </div>
        )}

        {activeView === 'stakes' && (
          <div className="stakes-container">
            {stakes.length === 0 ? (
              <div className="no-data">
                <p>No staking history found</p>
              </div>
            ) : (
              <div className="stakes-grid">
                {stakes.map((stake) => (
                  <div key={stake._id} className="stake-card">
                    <div className="stake-header">
                      <h3>{stake.plan}</h3>
                      <span className={`status ${stake.status}`}>
                        {stake.status}
                      </span>
                  </div>
                    <div className="stake-details">
                      <p><strong>User:</strong> {stake.user.email}</p>
                      <p><strong>Amount:</strong> ${stake.amount.toFixed(2)}</p>
                      <p><strong>Plan:</strong> {stake.plan}</p>
                      <p><strong>Duration:</strong> {stake.duration}</p>
                      <p><strong>APY:</strong> {stake.apy}%</p>
                      <p><strong>Rewards:</strong> ${stake.rewards.toFixed(2)}</p>
                      <p><strong>Start Date:</strong> {new Date(stake.startDate).toLocaleDateString()}</p>
                      <p><strong>End Date:</strong> {new Date(stake.endDate).toLocaleDateString()}</p>
                    </div>
                  </div>
                ))}
                  </div>
            )}
                </div>
        )}

        {activeView === 'users' && (
          <div className="users-container">
            {users.length === 0 ? (
              <div className="no-data">
                <p>No users found</p>
              </div>
            ) : (
              <div className="users-grid">
                {users.map((user) => (
                  <div key={user._id} className="user-card">
                    <div className="user-header">
                      <h3>{user.firstName} {user.lastName}</h3>
                      {user.isAdmin && <span className="admin-badge">Admin</span>}
                    </div>
                    <div className="user-details">
                      <p><strong>Email:</strong> {user.email}</p>
                      <p><strong>Username:</strong> {user.userName}</p>
                      <p><strong>Balance:</strong> ${user.balance.toFixed(2)}</p>
                      <p><strong>Total Profit:</strong> ${user.totalProfit.toFixed(2)}</p>
                      <p><strong>Total Deposit:</strong> ${user.totalDeposit.toFixed(2)}</p>
                      <p><strong>Total Withdrawal:</strong> ${user.totalWithdrawal.toFixed(2)}</p>
                      <p><strong>Referral Bonus:</strong> ${user.referralBonus.toFixed(2)}</p>
                      <p><strong>Joined:</strong> {new Date(user.createdAt).toLocaleDateString()}</p>
                    </div>
                    <div className="user-actions">
                      <button
                        className="fund-user-btn"
                        onClick={() => {
                          setSelectedUser(user);
                          setShowFundUserModal(true);
                        }}
                      >
                        <FaMoneyBillWave /> Fund User
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeView === 'crypto' && (
          <div className="crypto-assets-container">
            <div className="crypto-assets-header">
              <h2>Manage Crypto Assets</h2>
              <button 
                className="add-asset-btn"
                onClick={() => setShowAddAssetModal(true)}
              >
                Add New Asset
              </button>
            </div>

            {cryptoAssets.length === 0 ? (
              <div className="no-data">
                <p>No crypto assets found</p>
              </div>
            ) : (
              <div className="crypto-assets-grid">
                {cryptoAssets.map((asset) => (
                  <div key={asset._id} className="crypto-asset-card">
                    <div className="asset-header">
                      {asset.logoUrl && (
                        <img src={asset.logoUrl} alt={asset.name} className="asset-logo" />
                      )}
                      <h3>{asset.name}</h3>
                      <span className={`status ${asset.isActive ? 'active' : 'inactive'}`}>
                        {asset.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <div className="asset-details">
                      <p><strong>Symbol:</strong> {asset.symbol}</p>
                      <p><strong>Wallet Address:</strong> {asset.walletAddress}</p>
                      <p><strong>Last Updated:</strong> {new Date(asset.updatedAt).toLocaleDateString()}</p>
                    </div>
                    <div className="asset-actions">
                      <button
                        className="edit-btn"
                        onClick={() => {
                          Swal.fire({
                            title: 'Edit Crypto Asset',
                            html: `
                              <input id="name" class="swal2-input" placeholder="Name" value="${asset.name}">
                              <input id="symbol" class="swal2-input" placeholder="Symbol" value="${asset.symbol}">
                              <input id="walletAddress" class="swal2-input" placeholder="Wallet Address" value="${asset.walletAddress}">
                              <input id="logoUrl" class="swal2-input" placeholder="Logo URL" value="${asset.logoUrl}">
                            `,
                            focusConfirm: false,
                            showCancelButton: true,
                            preConfirm: () => {
                              return {
                                name: document.getElementById('name').value,
                                symbol: document.getElementById('symbol').value,
                                walletAddress: document.getElementById('walletAddress').value,
                                logoUrl: document.getElementById('logoUrl').value
                              }
                            }
                          }).then((result) => {
                            if (result.isConfirmed) {
                              handleUpdateAsset(asset._id, result.value);
                            }
                          });
                        }}
                      >
                        Edit
                      </button>
                      <button
                        className="toggle-btn"
                        onClick={() => handleUpdateAsset(asset._id, { isActive: !asset.isActive })}
                      >
                        {asset.isActive ? 'Deactivate' : 'Activate'}
                      </button>
                      <button
                        className="delete-btn"
                        onClick={() => handleDeleteAsset(asset._id)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {showAddAssetModal && (
        <div className="modal">
          <div className="modal-content">
            <h2>Add New Crypto Asset</h2>
            <div className="form-group">
              <input
                type="text"
                placeholder="Name"
                value={newAsset.name}
                onChange={(e) => setNewAsset({ ...newAsset, name: e.target.value })}
              />
            </div>
            <div className="form-group">
              <input
                type="text"
                placeholder="Symbol"
                value={newAsset.symbol}
                onChange={(e) => setNewAsset({ ...newAsset, symbol: e.target.value })}
              />
            </div>
            <div className="form-group">
              <input
                type="text"
                placeholder="Wallet Address"
                value={newAsset.walletAddress}
                onChange={(e) => setNewAsset({ ...newAsset, walletAddress: e.target.value })}
              />
            </div>
            <div className="form-group">
              <input
                type="text"
                placeholder="Logo URL"
                value={newAsset.logoUrl}
                onChange={(e) => setNewAsset({ ...newAsset, logoUrl: e.target.value })}
              />
            </div>
            <div className="modal-actions">
              <button onClick={() => setShowAddAssetModal(false)}>Cancel</button>
              <button onClick={handleAddAsset}>Add Asset</button>
            </div>
          </div>
        </div>
      )}

      {showFundUserModal && selectedUser && (
        <div className="modal">
          <div className="modal-content">
            <p>Fund User: </p>
            <p>{selectedUser.email}</p>
            <div className="form-group">
              <label>Current Balance: ${selectedUser.balance.toFixed(2)}</label>
              <input
                type="number"
                placeholder="Enter amount to fund"
                value={fundingAmount}
                onChange={(e) => setFundingAmount(e.target.value)}
                min="0"
                step="0.01"
              />
            </div>
            <div className="modal-actions">
              <button 
                className="cancel-btn"
                onClick={() => {
                  setShowFundUserModal(false);
                  setSelectedUser(null);
                  setFundingAmount('');
                }}
              >
                Cancel
              </button>
              <button 
                className="confirm-btn"
                onClick={handleManualFunding}
              >
                Fund User
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Admindashboard;
